/**
 * multipart/form-data 단일 파일 업로드 공용 헬퍼.
 *
 * ⚠️ 네이티브에서는 **CapacitorHttp(패치된 fetch)를 그대로 태워야** 한다(우회 금지).
 *    CapacitorHttp(capacitor.config.ts, enabled=true)의 fetch 패치는 body 가 FormData 면
 *    각 File 을 base64 로 인코딩(native-bridge convertFormData → type:'base64File')해
 *    네이티브 iOS URLSession 이 boundary 포함 **정상 multipart** 를 만들어 보낸다.
 *    크로스 오리진(app.template.kr→api.template.kr)도 네이티브라 CORS/preflight 무관, 대용량 OK.
 *
 *    핵심: **Content-Type 을 수동 지정하지 말 것.** 과거 `Content-Type: multipart/form-data`
 *    (boundary 없음)를 직접 박아서 Spring 이 HttpMediaTypeNotSupportedException(500)으로
 *    거부했다. Content-Type 을 비우면 네이티브가 boundary 를 생성한다.
 *
 *    ❌ 원본 fetch(window.CapacitorWebFetch) 로 우회하면 WKWebView 네이티브 fetch 의
 *       File 바디 업로드 한계에 걸려 `TypeError: Load failed` 로 실패한다(CapacitorHttp 가
 *       존재하는 바로 그 이유). 그래서 우회가 아니라 CapacitorHttp 를 써야 한다.
 *
 * 웹에서는 CapacitorHttp 가 없으므로 axios 경로(브라우저가 boundary 자동 생성)를 쓴다.
 */
import { client, userBaseUrl } from "./client";
import { isNative } from "../native";
import { useAuthStore } from "../store/authStore";
import { ApiError, isApiEnvelope } from "../lib/errors";
import i18n from "../i18n";

export async function uploadMultipart<T>(
  path: string,
  file: File,
  fields?: Record<string, string>,
): Promise<T> {
  const fd = new FormData();
  fd.append("file", file);
  if (fields) {
    for (const [key, value] of Object.entries(fields)) fd.append(key, value);
  }

  // 웹: axios 그대로 — 브라우저가 FormData 에 boundary 를 붙인다.
  if (!isNative()) {
    const res = await client.post<{ data: T }>(path, fd);
    return (res.data?.data ?? null) as T;
  }

  // 네이티브: 패치된 fetch(CapacitorHttp) 사용. Content-Type 미지정 → 네이티브가 multipart 생성.
  const token = useAuthStore.getState().accessToken;
  const headers: Record<string, string> = { "X-Client-Native": "1" };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await window.fetch(`${userBaseUrl()}${path}`, {
    method: "POST",
    headers,
    body: fd,
  });

  const body = (await res.json().catch(() => null)) as unknown;
  if (isApiEnvelope(body)) {
    if (body.code.startsWith("S")) return (body.data ?? null) as T;
    throw new ApiError(body.code, body.message, { httpStatus: res.status, data: body.data });
  }
  throw new ApiError(`E${res.status}`, i18n.t("error.uploadFailed"), { httpStatus: res.status });
}
